import { useState, useRef, useCallback } from "react";
import { Camera, Upload, Ticket, Store, Calendar, Tag, CreditCard, FileText, Info, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const PRIMARY = "#2D5BFF";

const VOUCHER_TYPES = [
  { value: "money_off", label: "Money off" },
  { value: "product_deal", label: "Product deal" },
  { value: "free_product", label: "Free product" },
  { value: "reward_voucher", label: "Reward voucher" },
  { value: "points_booster", label: "Points booster" },
  { value: "percentage_off", label: "Percentage off" },
];

const WHERE_OPTIONS = [
  { value: "in_store", label: "In store" },
  { value: "online", label: "Online" },
  { value: "both", label: "Both" },
];

const inputStyle = {
  width: "100%",
  border: "1px solid #E5E7EB",
  borderRadius: 12,
  padding: "12px 14px 12px 40px",
  fontSize: 15,
  fontFamily: "DM Sans, sans-serif",
  color: "#111827",
  outline: "none",
  backgroundColor: "#FFFFFF",
  minHeight: 44,
};

const labelStyle = {
  display: "block",
  color: "#374151",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "DM Sans, sans-serif",
  marginBottom: 6,
};

const InputIcon = ({ icon: Icon, top = 13 }) => (
  <Icon
    className="absolute left-3"
    style={{ top, width: 18, height: 18, color: "#9CA3AF", pointerEvents: "none" }}
  />
);


// ── Voucher cropper (same pattern as receipt cropper, zero dependencies) ──
const CropperOverlay = ({ imageSrc, onCrop, onSkip, isMobile }) => {
  const imgRef  = useRef(null);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const [crop, setCrop] = useState({ x: 5, y: 5, w: 90, h: 90 });

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const MIN = 15;

  const getPos = (e) => {
    const wrap = wrapRef.current;
    if (!wrap) return { x: 0, y: 0 };
    const rect = wrap.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return { x: ((cx - rect.left) / rect.width) * 100, y: ((cy - rect.top) / rect.height) * 100 };
  };

  const startDrag = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const p = getPos(e);
    dragRef.current = { type, startX: p.x, startY: p.y, startCrop: { ...crop } };

    const onMove = (ev) => {
      ev.preventDefault();
      if (!dragRef.current) return;
      const { startX, startY, startCrop: sc, type: t } = dragRef.current;
      const cur = getPos(ev);
      const dx = cur.x - startX, dy = cur.y - startY;
      setCrop(() => {
        let { x, y, w, h } = sc;
        if (t === 'move') { x = clamp(sc.x + dx, 0, 100 - sc.w); y = clamp(sc.y + dy, 0, 100 - sc.h); }
        else if (t === 'nw') { x = clamp(sc.x + dx, 0, sc.x + sc.w - MIN); y = clamp(sc.y + dy, 0, sc.y + sc.h - MIN); w = sc.w + (sc.x - x); h = sc.h + (sc.y - y); }
        else if (t === 'ne') { y = clamp(sc.y + dy, 0, sc.y + sc.h - MIN); h = sc.h + (sc.y - y); w = clamp(sc.w + dx, MIN, 100 - sc.x); }
        else if (t === 'sw') { x = clamp(sc.x + dx, 0, sc.x + sc.w - MIN); w = sc.w + (sc.x - x); h = clamp(sc.h + dy, MIN, 100 - sc.y); }
        else if (t === 'se') { w = clamp(sc.w + dx, MIN, 100 - sc.x); h = clamp(sc.h + dy, MIN, 100 - sc.y); }
        return { x, y, w, h };
      });
    };
    const onEnd = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
    document.addEventListener('mousemove', onMove, { passive: false });
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img) { onSkip(); return; }
    const cx = Math.round((crop.x / 100) * img.naturalWidth);
    const cy = Math.round((crop.y / 100) * img.naturalHeight);
    const cw = Math.round((crop.w / 100) * img.naturalWidth);
    const ch = Math.round((crop.h / 100) * img.naturalHeight);
    if (cw < 50 || ch < 50) { onSkip(); return; }
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    canvas.getContext('2d').drawImage(img, cx, cy, cw, ch, 0, 0, cw, ch);
    canvas.toBlob((blob) => {
      if (!blob) { onSkip(); return; }
      onCrop(new File([blob], 'cropped-voucher.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  const IMG_MAX_H = isMobile ? 'calc(100vh - 230px)' : '58vh';
  const corners = [
    { key: 'nw', cx: crop.x,          cy: crop.y },
    { key: 'ne', cx: crop.x + crop.w, cy: crop.y },
    { key: 'sw', cx: crop.x,          cy: crop.y + crop.h },
    { key: 'se', cx: crop.x + crop.w, cy: crop.y + crop.h },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: '#0B1120' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px 8px', flexShrink: 0 }}>
        <button onClick={onSkip} style={{ color: '#94A3B8', fontSize: 14, fontFamily: 'DM Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer', padding: 0, minWidth: 40 }}>Skip</button>
        <p style={{ flex: 1, textAlign: 'center', color: '#FFFFFF', fontSize: 17, fontWeight: 700, fontFamily: 'Manrope, sans-serif', margin: 0 }}>Crop voucher</p>
        <button onClick={handleConfirm} style={{ color: '#60A5FA', fontSize: 14, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', background: 'none', border: 'none', cursor: 'pointer', padding: 0, minWidth: 40, textAlign: 'right' }}>Done</button>
      </div>
      <p style={{ textAlign: 'center', color: '#475569', fontSize: 12, fontFamily: 'DM Sans, sans-serif', margin: '0 0 10px', flexShrink: 0 }}>Drag corners to frame the voucher</p>

      <div className="flex-1 flex items-center justify-center overflow-hidden" style={{ padding: '0 12px' }}>
        <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', maxHeight: IMG_MAX_H, lineHeight: 0, touchAction: 'none', userSelect: 'none' }}>
          <img ref={imgRef} src={imageSrc} alt="Crop" draggable={false}
            style={{ display: 'block', maxWidth: '100%', maxHeight: IMG_MAX_H, objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }} />

          {/* Dark overlay — 4 rects outside crop */}
          <div style={{ position: 'absolute', left: 0, top: 0, right: 0, height: `${crop.y}%`, background: 'rgba(0,0,0,0.62)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', left: 0, bottom: 0, right: 0, height: `${100 - crop.y - crop.h}%`, background: 'rgba(0,0,0,0.62)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', left: 0, top: `${crop.y}%`, width: `${crop.x}%`, height: `${crop.h}%`, background: 'rgba(0,0,0,0.62)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', right: 0, top: `${crop.y}%`, width: `${100 - crop.x - crop.w}%`, height: `${crop.h}%`, background: 'rgba(0,0,0,0.62)', pointerEvents: 'none' }} />

          {/* Crop border + drag-to-move interior */}
          <div
            style={{ position: 'absolute', left: `${crop.x}%`, top: `${crop.y}%`, width: `${crop.w}%`, height: `${crop.h}%`, border: '1.5px solid rgba(255,255,255,0.85)', cursor: 'move', touchAction: 'none', boxSizing: 'border-box' }}
            onMouseDown={(e) => startDrag(e, 'move')} onTouchStart={(e) => startDrag(e, 'move')}
          >
            {/* Thirds grid */}
            {['33%', '67%'].map(p => <div key={p} style={{ position: 'absolute', left: p, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.15)', pointerEvents: 'none' }} />)}
            {['33%', '67%'].map(p => <div key={p} style={{ position: 'absolute', top: p, left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.15)', pointerEvents: 'none' }} />)}
          </div>

          {/* Corner handles */}
          {corners.map(({ key, cx, cy }) => (
            <div key={key}
              style={{ position: 'absolute', left: `calc(${cx}% - 16px)`, top: `calc(${cy}% - 16px)`, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20, touchAction: 'none', cursor: `${key}-resize` }}
              onMouseDown={(e) => startDrag(e, key)} onTouchStart={(e) => startDrag(e, key)}
            >
              <div style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: '#FFFFFF', boxShadow: '0 2px 6px rgba(0,0,0,0.45)' }} />
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: isMobile ? '14px 20px 36px' : '14px 20px 20px', flexShrink: 0 }}>
        <button onClick={handleConfirm}
          style={{ width: '100%', height: 52, borderRadius: 16, background: 'linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)', color: '#FFFFFF', fontSize: 16, fontWeight: 700, fontFamily: 'DM Sans, sans-serif', border: 'none', cursor: 'pointer', boxShadow: '0 8px 20px rgba(45,91,255,0.3)', marginBottom: 10 }}>
          ✂︎ Crop &amp; Scan
        </button>
        <button onClick={onSkip}
          style={{ width: '100%', background: 'none', border: 'none', color: '#475569', fontSize: 14, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', padding: '6px 0' }}>
          Use full image instead →
        </button>
      </div>
    </div>
  );
};


export default function ScanVoucherContent({ onComplete, onSaved, isMobile = true }) {
  // ── State ────────────────────────────────────
  const [step, setStep] = useState("capture"); // capture | scanning | form | saving | done
  const [imagePreview, setImagePreview] = useState(null);
  const [scanError, setScanError] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Cropper state (same pattern as receipt)
  const [cropperSrc, setCropperSrc] = useState(null);
  const pendingRawFileRef = useRef(null);

  // Form fields
  const [retailer, setRetailer] = useState("");
  const [description, setDescription] = useState("");
  const [voucherType, setVoucherType] = useState("money_off");
  const [value, setValue] = useState("");
  const [valueLabel, setValueLabel] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [minSpend, setMinSpend] = useState("");
  const [productName, setProductName] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [barcodeNumber, setBarcodeNumber] = useState("");
  const [whereToUse, setWhereToUse] = useState("");
  const [linkedCard, setLinkedCard] = useState("");
  const [conditions, setConditions] = useState("");
  const [notes, setNotes] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [tempVoucherId, setTempVoucherId] = useState("");

  // ── Process file (scan it) ───────────────────
  const processFile = useCallback(async (file) => {
    // Preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    setStep("scanning");
    setScanError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/scan-voucher`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });

      const { success, data, image_url, temp_voucher_id, error } = res.data;

      if (image_url) setImageUrl(image_url);
      if (temp_voucher_id) setTempVoucherId(temp_voucher_id);

      if (!success) {
        setScanError(error || "Could not read voucher. Fill in the details manually.");
        setStep("form");
        return;
      }

      // Pre-fill form
      if (data.retailer) setRetailer(data.retailer);
      if (data.description) setDescription(data.description);
      if (data.voucher_type) setVoucherType(data.voucher_type);
      if (data.value != null) setValue(String(data.value));
      if (data.value_label) setValueLabel(data.value_label);
      if (data.currency) setCurrency(data.currency);
      if (data.min_spend != null) setMinSpend(String(data.min_spend));
      if (data.product_name) setProductName(data.product_name);
      if (data.expiry_date) setExpiryDate(data.expiry_date);
      if (data.barcode_number) setBarcodeNumber(data.barcode_number);
      if (data.where_to_use) setWhereToUse(data.where_to_use);
      if (data.linked_card) setLinkedCard(data.linked_card);
      if (data.conditions) setConditions(data.conditions);

      setStep("form");
    } catch (err) {
      console.error("Scan voucher error:", err);
      setScanError("Scan failed. Fill in the details manually.");
      setStep("form");
    }
  }, []);

  // ── Image capture → show cropper ─────────────
  const handleFile = useCallback((file) => {
    if (!file) return;
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File too large (max 20 MB)");
      return;
    }

    // PDF: skip cropper, go straight to scan
    if (file.type === "application/pdf") {
      processFile(file);
      return;
    }

    // All other images: show cropper
    pendingRawFileRef.current = file;
    const reader = new FileReader();
    reader.onloadend = () => setCropperSrc(reader.result);
    reader.readAsDataURL(file);
  }, [processFile]);

  // ── Crop confirmed ──────────────────────────
  const handleCropConfirm = async (croppedFile) => {
    setCropperSrc(null);
    pendingRawFileRef.current = null;
    await processFile(croppedFile);
  };

  // ── Crop skipped: use original file ─────────
  const handleCropSkip = async () => {
    const file = pendingRawFileRef.current;
    setCropperSrc(null);
    pendingRawFileRef.current = null;
    if (file) await processFile(file);
  };

  const onCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const onFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  // ── Save ─────────────────────────────────────
  const handleSave = async () => {
    if (!retailer.trim()) {
      toast.error("Enter the retailer name");
      return;
    }
    if (!description.trim()) {
      toast.error("Enter the voucher description");
      return;
    }

    setStep("saving");

    try {
      const payload = {
        retailer: retailer.trim(),
        description: description.trim(),
        voucher_type: voucherType,
        value: value ? parseFloat(value) : null,
        value_label: valueLabel || null,
        currency: currency || "GBP",
        min_spend: minSpend ? parseFloat(minSpend) : null,
        product_name: productName || null,
        expiry_date: expiryDate || null,
        barcode_number: barcodeNumber || null,
        where_to_use: whereToUse || null,
        linked_card: linkedCard || null,
        conditions: conditions || null,
        notes: notes || null,
        image_url: imageUrl || null,
      };

      await axios.post(`${API}/vouchers`, payload, { withCredentials: true });

      setStep("done");
      toast.success("Voucher saved!");

      if (onSaved) onSaved();
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 600);
    } catch (err) {
      console.error("Save voucher error:", err);
      const detail = err.response?.data?.detail;
      if (detail?.validation_errors) {
        const msgs = Object.values(detail.validation_errors);
        toast.error(msgs[0] || "Please check your inputs");
      } else {
        toast.error("Could not save voucher. Please try again.");
      }
      setStep("form");
    }
  };

  // ── Skip scan (manual entry) ─────────────────
  const handleManualEntry = () => {
    setStep("form");
  };

  // ════════════════════════════════════════════════
  //  STEP: CAPTURE
  // ════════════════════════════════════════════════

  if (step === "capture") {
    return (
      <div className="flex flex-col items-center">
        {/* Photo area */}
        <div
          className="w-full flex flex-col items-center justify-center"
          style={{
            border: "2px dashed #C7D2FE",
            borderRadius: 18,
            padding: isMobile ? "32px 20px" : "40px 20px",
            backgroundColor: "#F8FAFC",
            marginBottom: 20,
          }}
        >
          <div
            className="flex items-center justify-center mb-4"
            style={{ width: 56, height: 56, borderRadius: 999, backgroundColor: "rgba(45,91,255,0.1)" }}
          >
            <Ticket className="w-7 h-7" style={{ color: PRIMARY }} />
          </div>
          <p style={{ color: "#374151", fontSize: 15, fontFamily: "DM Sans, sans-serif", textAlign: "center" }}>
            {isMobile ? "Tap to photograph your voucher" : "Drag a voucher here, or use a button below"}
          </p>

          <div className="flex items-center gap-3 mt-5">
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="flex items-center gap-2 rounded-full"
              style={{ backgroundColor: PRIMARY, color: "#FFFFFF", padding: "12px 22px", fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}
            >
              <Camera className="w-4 h-4" />
              Camera
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 rounded-full"
              style={{ backgroundColor: "#FFFFFF", color: "#374151", border: "1px solid #D1D5DB", padding: "12px 22px", fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
          </div>

          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={onCameraCapture} className="hidden" />
          <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={onFileUpload} className="hidden" />
        </div>

        {/* Tip */}
        <div
          className="w-full flex items-start gap-2.5"
          style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: "12px 14px", marginBottom: 20 }}
        >
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#D97706" }} />
          <p style={{ color: "#92400E", fontSize: 13, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>
            For best results, lay flat and photograph from directly above. Works with paper vouchers and digital screenshots.
          </p>
        </div>

        {/* Manual entry link */}
        <button onClick={handleManualEntry} style={{ color: "#6B7280", fontSize: 14, fontFamily: "DM Sans, sans-serif", textDecoration: "underline", textUnderlineOffset: 3 }}>
          Enter details manually instead
        </button>

        {/* Cropper overlay (renders on top when cropperSrc is set) */}
        {cropperSrc && (
          <CropperOverlay
            imageSrc={cropperSrc}
            onCrop={handleCropConfirm}
            onSkip={handleCropSkip}
            isMobile={isMobile}
          />
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════
  //  STEP: SCANNING
  // ════════════════════════════════════════════════

  if (step === "scanning") {
    return (
      <div className="flex flex-col items-center py-12">
        {imagePreview && (
          <img src={imagePreview} alt="Voucher"
            style={{ width: 200, height: 200, objectFit: "cover", borderRadius: 16, marginBottom: 24, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}
          />
        )}
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: PRIMARY }} />
        <p style={{ color: "#111827", fontSize: 17, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Scanning details…</p>
        <p className="mt-2" style={{ color: "#6B7280", fontSize: 14, fontFamily: "DM Sans, sans-serif" }}>Reading retailer, value, and expiry date</p>
      </div>
    );
  }

  // ════════════════════════════════════════════════
  //  STEP: DONE
  // ════════════════════════════════════════════════

  if (step === "done") {
    return (
      <div className="flex flex-col items-center py-12">
        <CheckCircle2 className="w-16 h-16 mb-4" style={{ color: "#22C55E" }} />
        <p style={{ color: "#111827", fontSize: 20, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Voucher saved!</p>
      </div>
    );
  }

  // ════════════════════════════════════════════════
  //  STEP: FORM (+ saving)
  // ════════════════════════════════════════════════

  const isSaving = step === "saving";

  return (
    <div>
      {/* Scan error banner */}
      {scanError && (
        <div className="flex items-start gap-2 mb-5" style={{ backgroundColor: "#FFF7ED", border: "1px solid #FDE68A", borderRadius: 12, padding: "12px 14px" }}>
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#D97706" }} />
          <p style={{ color: "#92400E", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>{scanError}</p>
        </div>
      )}

      {/* Image preview thumbnail */}
      {imagePreview && (
        <div className="flex items-center gap-3 mb-5">
          <img src={imagePreview} alt="Voucher" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 10, border: "1px solid #E5E7EB" }} />
          <p style={{ color: "#6B7280", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>Photo attached — check the details below</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Retailer */}
        <div>
          <label style={labelStyle}>Retailer</label>
          <div className="relative">
            <InputIcon icon={Store} />
            <input type="text" value={retailer} onChange={(e) => setRetailer(e.target.value)}
              placeholder="e.g. Tesco, Sainsbury's" style={{ ...inputStyle, fontSize: 16 }} disabled={isSaving} />
          </div>
        </div>

        {/* Description */}
        <div>
          <label style={labelStyle}>What you get</label>
          <div className="relative">
            <InputIcon icon={Tag} />
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. £7.50 off when you spend £50" style={{ ...inputStyle, fontSize: 16 }} disabled={isSaving} />
          </div>
        </div>

        {/* Type + Value row */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label style={labelStyle}>Type</label>
            <select value={voucherType} onChange={(e) => setVoucherType(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 14, appearance: "none", cursor: "pointer" }} disabled={isSaving}>
              {VOUCHER_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
            </select>
          </div>
          <div style={{ width: isMobile ? 100 : 120 }}>
            <label style={labelStyle}>Value</label>
            <input type="text" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)}
              placeholder="£" style={{ ...inputStyle, paddingLeft: 14, textAlign: "right", fontSize: 16 }} disabled={isSaving} />
          </div>
        </div>

        {/* Expiry date */}
        <div>
          <label style={labelStyle}>Expiry date</label>
          <div className="relative">
            <InputIcon icon={Calendar} />
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
              style={{ ...inputStyle, fontSize: 16, textAlign: "left" }} disabled={isSaving} />
          </div>
        </div>

        {/* Barcode number */}
        <div>
          <label style={labelStyle}>Barcode number <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(optional)</span></label>
          <div className="relative">
            <InputIcon icon={CreditCard} />
            <input type="text" value={barcodeNumber} onChange={(e) => setBarcodeNumber(e.target.value)}
              placeholder="Scanned automatically" style={inputStyle} disabled={isSaving} />
          </div>
        </div>

        {/* Minimum spend */}
        <div>
          <label style={labelStyle}>Minimum spend <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(optional)</span></label>
          <div className="relative">
            <InputIcon icon={Tag} />
            <input type="text" inputMode="decimal" value={minSpend} onChange={(e) => setMinSpend(e.target.value)}
              placeholder="e.g. 50.00" style={inputStyle} disabled={isSaving} />
          </div>
        </div>

        {/* Where to use */}
        <div>
          <label style={labelStyle}>Where to use <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(optional)</span></label>
          <div className="flex gap-2">
            {WHERE_OPTIONS.map((opt) => (
              <button key={opt.value} onClick={() => setWhereToUse(whereToUse === opt.value ? "" : opt.value)}
                className="flex-1 rounded-full text-center transition-colors"
                style={{
                  backgroundColor: whereToUse === opt.value ? PRIMARY : "#FFFFFF",
                  color: whereToUse === opt.value ? "#FFFFFF" : "#374151",
                  border: whereToUse === opt.value ? "none" : "1px solid #E5E7EB",
                  padding: "10px 14px", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif",
                }}
                disabled={isSaving}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Linked loyalty card */}
        <div>
          <label style={labelStyle}>Linked loyalty card <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(optional)</span></label>
          <div className="relative">
            <InputIcon icon={CreditCard} />
            <input type="text" value={linkedCard} onChange={(e) => setLinkedCard(e.target.value)}
              placeholder="e.g. Clubcard, Nectar" style={inputStyle} disabled={isSaving} />
          </div>
        </div>

        {/* Conditions */}
        <div>
          <label style={labelStyle}>Conditions <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(optional)</span></label>
          <div className="relative">
            <InputIcon icon={FileText} />
            <textarea value={conditions} onChange={(e) => setConditions(e.target.value)}
              placeholder="T&Cs or restrictions" rows={2} style={{ ...inputStyle, resize: "vertical" }} disabled={isSaving} />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label style={labelStyle}>Notes <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(optional)</span></label>
          <div className="relative">
            <InputIcon icon={FileText} />
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Any personal notes..." rows={2} style={{ ...inputStyle, resize: "vertical" }} disabled={isSaving} />
          </div>
        </div>
      </div>

      {/* Save button */}
      <button onClick={handleSave} disabled={isSaving || !retailer.trim() || !description.trim()}
        className="w-full flex items-center justify-center gap-2 mt-6"
        style={{
          backgroundColor: isSaving || !retailer.trim() || !description.trim() ? "#93A3B8" : PRIMARY,
          color: "#FFFFFF", borderRadius: 14, padding: "16px", fontSize: 16, fontWeight: 700, fontFamily: "DM Sans, sans-serif",
          boxShadow: isSaving ? "none" : "0 4px 14px rgba(45,91,255,0.3)", cursor: isSaving ? "not-allowed" : "pointer",
        }}
        data-testid="save-voucher-btn">
        {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
        {isSaving ? "Saving…" : "Save Voucher"}
      </button>
    </div>
  );
}